/* eslint-disable sonarjs/cognitive-complexity */
import { BaSeActionItem, BaSeTheme } from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import { format } from 'date-fns';
import React from 'react';

import { useAnswerController } from '../../../../../../hooks/controllers/answer-controller';
import { useEmailController } from '../../../../../../hooks/controllers/email-controller';
import { useInterviewController } from '../../../../../../hooks/controllers/interview-controller';
import { useReportController } from '../../../../../../hooks/controllers/report-controller';
import { useMessage } from '../../../../../../hooks/messages/useMessage';
import { useQuizProfileService } from '../../../../../../hooks/services/quiz-profile-service';
import { useCandidateInterview } from '../../hook/useCandidateInterview';
import { Container } from './selection-actions-buttons';

// import { Container } from './styles';

function SelectionActionsButtons({
  isAllSelected,
  itemsSelectTable = [],
  moduleSelected,
}) {
  const { hasRole } = Sebrae.useAuth();

  const { handleSuccess, handleError, handleAttention } = useMessage();
  const { sendEmailSubscription } = useEmailController();
  const { downloadPdf, getClassReport } = useReportController();
  const { resendAnswerInterviewmutiple } = useAnswerController();

  const { fetchData, getDownloadCertificate } = useInterviewController();

  const reloadData = React.useCallback(() => {
    fetchData({
      filter: {},
      page: 0,
      sort: { type: 'DESC' },
    });
  }, [fetchData]);
  const { openDialogGenerateCertificate, openDialogSendMultipleCertificate } =
    useCandidateInterview();

  const { sendQuizLote } = useQuizProfileService();

  const exportClassReport = React.useCallback(
    (interviews) => {
      if (
        interviews.filter((interview) => interview.statusInterview !== 'APTO')
          .length
      ) {
        handleAttention(
          'Relatório permitido somente para candidatos com o status da entrevista "Finalizada" ou "Certificada" e resultado "Apto".',
        );
        return;
      }

      if (interviews.length > 40) {
        handleAttention('Limite máximo de 40 candidatos por relatório.');
        return;
      }

      getClassReport(interviews.map((s) => s.id)).then((pdf) => {
        const nameReport = `Relatório da Turma-${format(
          new Date(),
          'dd-MM-yyyy',
        )}.pdf`;
        pdf && downloadPdf(pdf, nameReport);
      });
    },
    [downloadPdf, getClassReport, handleAttention],
  );

  const generateMultipleCertificate = React.useCallback(
    (isAllSelected, interviews, reloadData) => {
      const totalEntrevistas = interviews.length;

      if (isAllSelected || totalEntrevistas > 40) {
        handleError(
          'Somente é permitido selecionar um máximo de 40 candidatos para geração do certificado em lote.',
        );
        return;
      }

      const listaNaoAptos = interviews.filter(
        (interview) =>
          interview.statusInterview !== 'APTO' ||
          (interview.statusCompletingInterview !== 'FINALIZADA' &&
            interview.statusCompletingInterview !== 'EXPIRADA'),
      ).length;
      if (listaNaoAptos > 0) {
        handleError(
          'Geração dos certificados permitida somente para os candidatos com status "Finalizada" ou "Expirada" e resultado "Apto".',
        );
        return;
      }

      openDialogGenerateCertificate(interviews, reloadData);
    },
    [handleError, openDialogGenerateCertificate],
  );

  const sendMultipleCertificate = React.useCallback(
    (isAllSelected, interviews, reloadData) => {
      const hasNotCertificate = interviews.filter(
        (interview) => interview.statusCompletingInterview !== 'CERTIFICADA',
      );
      if (hasNotCertificate.length > 0) {
        handleAttention(
          'O envio do certificado digital é permitido somente para os candidatos com status da entrevista "Certificada".',
        );
        return;
      }

      const totalEntrevistas = interviews.length;
      if (isAllSelected || totalEntrevistas > 100) {
        handleError(
          'Somente é permitido selecionar um máximo de 100 candidatos para o envio do certificado em lote.',
        );
        return;
      }

      openDialogSendMultipleCertificate(interviews, reloadData);
    },
    [handleAttention, handleError, openDialogSendMultipleCertificate],
  );

  const printMultipleCertificate = React.useCallback(
    (isAllSelected, interviews, reloadData) => {
      const totalEntrevistas = interviews.length;
      const hasNotCertificate = interviews.filter(
        (interview) => interview.statusCompletingInterview !== 'CERTIFICADA',
      );

      const idList = interviews.map((interview) => interview.id);

      if (hasNotCertificate.length > 0) {
        handleError(
          'A impressão dos certificados é permitida somente para os candidatos com status "Certificada".',
        );
        return;
      }

      if (isAllSelected || totalEntrevistas > 40) {
        handleAttention(
          'Somente é permitido selecionar um máximo de 40 candidatos para impressão dos certificados em lote.',
        );
        return;
      }

      getDownloadCertificate(idList).then((response) => {
        response && downloadPdf(response, 'certificado.pdf');
      });
    },
    [downloadPdf, getDownloadCertificate, handleAttention, handleError],
  );

  const sendInterviewReprocess = React.useCallback(
    (idsList) => {
      resendAnswerInterviewmutiple(idsList)
        .then(() =>
          handleSuccess(
            'Solicitação de reprocessamento da(s) entrevista(s) enviada com sucesso.',
          ),
        )
        .catch((error) => handleError(error));
    },
    [resendAnswerInterviewmutiple, handleError, handleSuccess],
  );

  const checkInterviewsForNonErrorResult = React.useCallback(
    (data) => {
      const nonErrorResult = data.filter(
        (it) => it.statusInterview !== 'ERRO_DE_PROCESSAMENTO',
      );

      if (nonErrorResult.length > 0) {
        handleAttention(
          'Reprocessamento permitido somente para as entrevistas com resultado "Erro de Processamento".',
        );
        return;
      }
      const idsList = data.map((it) => it.id);
      sendInterviewReprocess(idsList);
    },
    [handleAttention, sendInterviewReprocess],
  );

  const sendEmailAutoavaliationInLote = React.useCallback(
    async (interviews) => {
      try {
        if (isAllSelected || interviews.length > 100) {
          handleAttention(
            'Somente é permitido selecionar um máximo de 100 entrevistas para o envio do e-mail de autoavaliação em lote.',
          );
          return;
        }
        const filterInterview = interviews.filter(
          (interview) =>
            interview.statusCompletingInterview !== 'FINALIZADA' ||
            interview.statusInterview !== 'APTO',
        );

        if (filterInterview.length > 0) {
          handleAttention(
            'O envio do e-mail de autoavaliação em lote somente é permitido para as entrevistas com status "Finalizada" e resultado "Apto".',
          );
          return;
        }

        await sendQuizLote(
          interviews.map((item) => `interviews=${item.id}`).join('&'),
        );
        handleSuccess('E-mail de autoavaliação enviado com sucesso!');
      } catch (error) {
        handleError(
          error ? error?.message : 'Erro ao enviar e-mails de autoavaliação.',
        );
      }
    },
    [handleAttention, handleError, handleSuccess, isAllSelected, sendQuizLote],
  );

  const createOption = (icon, text, type, color, callback) => ({
    icon,
    text,
    type,
    color,
    callback,
  });

  const defaultOptions = [
    createOption(
      'envelope',
      'Enviar e-mail de inscrição',
      null,
      BaSeTheme.colors.institucionais.cinzaSebrae45,
      () => {
        sendEmailSubscription({
          sendAll: isAllSelected,
          idsInterviews: itemsSelectTable.map((s) => s.id),
        }).then(() => {
          reloadData();
        });
      },
    ),
    moduleSelected?.id === 2 &&
      createOption(
        'envelope',
        'Enviar e-mail de autoavaliação',
        'tertiary',
        BaSeTheme.colors.institucionais.cinzaSebrae45,
        () => {
          sendEmailAutoavaliationInLote(itemsSelectTable);
        },
      ),
    createOption(
      'file-export',
      'Gerar relatório de perfil',
      null,
      BaSeTheme.colors.institucionais.cinzaSebrae45,
      () => {
        exportClassReport(itemsSelectTable);
      },
    ),
    createOption(
      'newspaper',
      'Gerar certificado',
      null,
      BaSeTheme.colors.institucionais.cinzaSebrae45,
      () => {
        generateMultipleCertificate(
          isAllSelected,
          itemsSelectTable,
          reloadData,
        );
      },
    ),
    createOption(
      'newspaper',
      'Enviar certificado',
      null,
      BaSeTheme.colors.institucionais.cinzaSebrae45,
      () => {
        sendMultipleCertificate(isAllSelected, itemsSelectTable, reloadData);
      },
    ),
    createOption(
      'print',
      'Imprimir certificado',
      null,
      BaSeTheme.colors.institucionais.cinzaSebrae45,
      () => {
        printMultipleCertificate(isAllSelected, itemsSelectTable, reloadData);
      },
    ),

    hasRole('ADMINISTRADOR') &&
      moduleSelected?.id !== 2 &&
      createOption(
        'system/arrow-go-back-line',
        'Reprocessar',
        'tertiary',
        BaSeTheme.colors.institucionais.cinzaSebrae45,
        () => {
          checkInterviewsForNonErrorResult(itemsSelectTable);
        },
      ),
  ].filter(Boolean);

  return (
    <Container>
      {
        <BaSeActionItem
          type="tertiary"
          buttonType="base-button"
          position="top-right"
          descriptionIcon="Lee"
          minWidth="310px"
          color={BaSeTheme.colors.institucionais.azulSebrae10}
          customButtonProps={{
            value: 'Ações em lote',
            leftIcon: 'ellipsis-v',
            descriptionRightIcon: 'button.description',
          }}
          items={defaultOptions}
          closeOnAction={true}
          isDisabled={
            isAllSelected || itemsSelectTable.length > 0 ? false : true
          }
        />
      }
    </Container>
  );
}

export default SelectionActionsButtons;
